import api from "../api";
import { GET_ERRORS } from "../errorServices/errorTypes";
import {
  QUOTE_REQUESTED,
  QUOTE_RECEIVED,
  QUOTE_FAILED,
  CLEAR_URL,
  QUOTES_REQUESTED,
  QUOTES_RECEIVED,
  QUOTES_FAILED,
  CLEAR_URLS,
  GET_FOLDERS,
  GET_PICTURES,
  CREATE_FOLDER,
  CLEAR_FOLDER,
  SET_PATH,
} from "./ImageTypes";
import { asCaught } from "../caughtError";

export const uploadImageAction = (base64EncodedImage, path) => async (dispatch) => {
  dispatch({
    type: QUOTE_REQUESTED,
  });
  try {
    const response = await fetch(
      "/api/image/upload",
      {
        method: "POST",
        body: JSON.stringify({ data: base64EncodedImage, path: path }),
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );
    // fetch does not reject on a non-2xx response, so the old chain treated a 500 as a
    // successful QUOTE_RECEIVED. Check response.ok and re-throw so the caller does not
    // save the variable / show success on a failed upload (SCRUM-815). Mirrors
    // uploadImagesAction.
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to upload image");
    }
    const data = await response.json();
    dispatch({
      type: QUOTE_RECEIVED,
      payload: data,
    });
    return data;
  } catch (error) {
    dispatch({
      type: QUOTE_FAILED,
      payload: error,
    });
    throw error;
  }
};

// SCRUM-1594: set (or clear, with "") the alt stored on an existing Cloudinary
// resource. Same fetch + response.ok pattern as uploadImageAction above; resolves to
// the server body `{ public_id, context }` and re-throws on a non-2xx / network error
// so the panel can show the reason instead of a false "Saved".
export const updateImageAltAction = (public_id, alt) => async (dispatch) => {
  try {
    const response = await fetch("/api/image/updateAlt", {
      method: "POST",
      body: JSON.stringify({ public_id, alt }),
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData?.errors?.[0]?.msg || errorData?.message || "Failed to update alt text"
      );
    }
    return await response.json();
  } catch (error) {
    dispatch({
      type: GET_ERRORS,
      payload: { message: asCaught(error)?.message },
    });
    throw error;
  }
};

export const clearUrl = () => (dispatch) => {
  dispatch({
    type: CLEAR_URL,
  });
};


export const uploadImagesAction = (index, base64EncodedImage, path) => async (dispatch) => {
  dispatch({
    type: QUOTES_REQUESTED,
  });

  try {
    // NB: no env guard here anymore. The old `!process.env.REACT_APP_SERVER_URL`
    // check threw on ECS, where that var is empty ON PURPOSE (deploy injects ""
    // so calls are same-origin) — breaking multi-image upload in deployed
    // environments while single-image upload worked. URLs are relative now, so
    // there is nothing to check (SCRUM-920).

    // Make the API call
    const response = await fetch("/api/image/upload", {
      method: "POST",
      body: JSON.stringify({ data: base64EncodedImage, path }),
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    // Validate the response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // Handle non-JSON responses
      throw new Error(errorData.message || "Failed to upload image");
    }

    // Parse JSON response
    const data = await response.json();

    // Dispatch success action
    dispatch({
      type: QUOTES_RECEIVED,
      payload: { index, data },
    });

    return data; // Return the data for further processing
  } catch (error) {
    console.error("Image upload failed:", error);

    // Dispatch failure action
    dispatch({
      type: QUOTES_FAILED,
      payload: asCaught(error).message,
    });

    throw error; // Re-throw the error for higher-level error handling
  }
};


export const clearUrls = () => (dispatch) => {
  dispatch({
    type: CLEAR_URLS,
  });
};

// Get Folders — also RESOLVES to res.data so a caller can key the result to the
// path it fetched (used by useMediaBrowser for race-free per-path caching). The
// GET_FOLDERS dispatch is unchanged; on error resolves to [] (and dispatches GET_ERRORS).
export const getFolders = (public_id) => (dispatch) => {
  return api
    .post(
      "/api/image/searchFolder",
      public_id
    )
    .then((res) => {
      dispatch({
        type: GET_FOLDERS,
        payload: res.data,
      });
      return res.data;
    })
    .catch((err) => {
      dispatch({
        type: GET_ERRORS,
        payload: err.response?.data, // the body ({errors:[{msg}]}), not the whole
        // axios response — matches addFolder and what getErrorMessage expects, so
        // a real failure shows its message instead of the generic fallback.
      });
      return undefined; // signal failure (not an empty result) so the caller does not cache it
    });
};
// Get Pictures — resolves to res.data (see getFolders); [] on error.
export const getPictures = (folder) => (dispatch) => {
  return api
    .post(
      "/api/image/getImagesByFolder",
      folder
    )
    .then((res) => {
      dispatch({
        type: GET_PICTURES,
        payload: res.data,
      });
      return res.data;
    })
    .catch((err) => {
      dispatch({
        type: GET_ERRORS,
        payload: err.response?.data, // body, not the whole axios response (see getFolders)
      });
      return undefined; // signal failure (not an empty result) so the caller does not cache it
    });
};

//Add Folder — resolves to the created folder doc (res.data) so the caller can
// prepend it to the current folder listing; null on error. CREATE_FOLDER dispatch unchanged.
export const addFolder = (public_id) => (dispatch) => {
  //public_id : p2cms/FR     ----> creating folder FR in p2cms folder
  return api
    .post("/api/image/createFolder", {
      public_id: public_id,
    })
    .then((res) => {
      dispatch({
        type: CREATE_FOLDER,
        payload: res.data,
      });
      return res.data;
    })

    .catch((err) => {
      // SCRUM-1317: was `err.response.data` unguarded. axios rejects with no
      // `response` on a network/DNS/timeout failure, so this catch threw its own
      // TypeError -- escaping the chain as an unhandled rejection, dispatching
      // nothing, and skipping the `return null` this thunk's own comment promises.
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      });
      return null;
    });
};
export const clearFolder = () => (dispatch) => {
  dispatch({
    type: CLEAR_FOLDER,
  });
};

export const setCloudinaryPath = (path) => (dispatch) => {
  dispatch({
    type: SET_PATH,
    payload: path,
  });
};

export const clearCloudinaryPath = () => (dispatch) => {
  dispatch({
    type: SET_PATH,
    payload: "",
  });
}
